import React, { useMemo, useEffect, useState } from 'react';
import { Restaurant } from '../types/restaurant';
import { getCurrentLanguage } from '../config/languageConfig';
import useTranslation from '../hooks/useTranslation';
import { formatPrice as formatPriceWithCurrency, SupportedCurrency, getCurrencyFromLanguage } from '../utils/currencyUtils';
import { getDeliveryStatus, DeliveryStatus } from '../utils/deliveryUtils';

interface RestaurantDetailCardProps {
  restaurant: Restaurant;
  currency?: SupportedCurrency;
  isFromCategory?: boolean; // 카테고리 목록에서 표시되는지 여부
}

const RestaurantDetailCard: React.FC<RestaurantDetailCardProps> = ({ 
  restaurant, 
  currency,
  isFromCategory = false
}) => {
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [deliveryStatus, setDeliveryStatus] = useState<DeliveryStatus>({ canOrderNow: false, canOrderLater: false });
  const { t } = useTranslation();
  
  // 배달 상태 조회
  useEffect(() => {
    const fetchDeliveryStatus = async () => {
      try {
        const status = await getDeliveryStatus();
        setDeliveryStatus(status);
      } catch (error) {
        console.warn('[RestaurantDetailCard] Failed to get delivery status:', error);
      }
    };
    
    fetchDeliveryStatus();
  }, []);
  
  // 현재 언어에 맞는 통화 설정
  const effectiveCurrency = useMemo(() => {
    return getCurrencyFromLanguage(currentLang);
  }, [currentLang]);
  
  // gift-shop-1에 대한 특별한 텍스트 처리
  const getChatText = () => {
    switch (currentLang) {
      case 'ja':
        return 'チャットでご相談ください';
      case 'zh-TW':
        return '使用聊天功能聯繫我們';
      case 'zh-CN':
        return '通过聊天联系我们';
      case 'en':
      default:
        return 'Chat with us';
    }
  };

  // gift-shop-1에 대한 설명 텍스트
  const getGiftShopDescription = () => {
    switch (currentLang) {
      case 'ja':
        return (
          <div>
            <div className="text-lg">🚀 <span className="font-bold"> 本日ご注文で、</span><span className="text-red-600 font-bold">明日お届け </span> 🚀</div>
            <br />
            <span className="font-bold">お探しのギフトや商品が見つかりませんか？</span>
            <br />
            <span className="font-bold">チャットでお気軽にご相談ください！</span>
          </div>
        );
      case 'zh-TW':
        return (
          <div>
            <div className="text-lg">🚀 <span className="font-bold"> 今天下单，</span><span className="text-red-600 font-bold">明天送达 </span> 🚀</div>
            <br />
            <span className="font-bold">找不到想要的禮品或商品嗎？</span>
            <br />
            <span className="font-bold">歡迎使用聊天功能聯繫我們，我們樂意協助您！</span>
          </div>
        );
      case 'zh-CN':
        return (
          <div>
            <div className="text-lg">🚀 <span className="font-bold"> 今天下单，</span><span className="text-red-600 font-bold">明天送达 </span> 🚀</div>
            <br />
            <span className="font-bold">找不到您想要的礼品或商品吗？</span>
            <br />
            <span className="font-bold">欢迎通过聊天联系我们，我们乐意为您服务！</span>
          </div>
        );
      case 'en':
      default:
        return (
          <div>
            <div className="text-lg">🚀<span className="font-bold"> Order today, </span><span className="text-red-600 font-bold">get it tomorrow </span>🚀</div>
            <br />
            <span className="font-bold">Can't find the gift or item you're looking for?</span>
            <br />
            <span className="font-bold">Chat with us</span>—we're here to help!
          </div>
        );
    }
  };

  // 채널톡 열기 함수
  const openChannelTalk = () => {
    if ((window as any).ChannelIO) {
      (window as any).ChannelIO('show');
    }
  };
  
  // 언어 변경 감지를 위한 이벤트 리스너 추가
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };
    
    // 언어 변경 이벤트 리스너 등록
    window.addEventListener('language-changed', handleLanguageChange);
    
    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('language-changed', handleLanguageChange);
    };
  }, []);
  
  // 현재 언어에 맞는 이름과 설명 계산
  const { displayName, displayDescription } = useMemo(() => {
    // 카테고리 목록에서만 프랜차이즈명 우선 사용 (언어와 관계없이 영문으로 표시)
    if (isFromCategory && restaurant.franchiseName) {
      return {
        displayName: restaurant.franchiseName, // franchiseName은 항상 영문으로 표시
        displayDescription: restaurant.introductionTitle || ''
      };
    }
    
    // 기본값 설정
    let name = restaurant.nameEn || '[Shop Name]';
    let description = restaurant.introductionTitle || '';

    // 현재 언어에 따라 적절한 필드 선택
    switch (currentLang) {
      case 'ko':
        return {
          displayName: restaurant.nameKo || restaurant.nameEn || name,
          displayDescription: restaurant.introductionTitleKo || restaurant.introductionTitleEn || description
        };
      case 'ja':
        return {
          displayName: restaurant.nameJa || restaurant.nameEn || name,
          displayDescription: restaurant.introductionTitleJa || restaurant.introductionTitleEn || description
        };
      case 'zh-CN':
        return {
          displayName: restaurant.nameZhCn || restaurant.nameEn || name,
          displayDescription: restaurant.introductionTitleZhCn || restaurant.introductionTitleEn || description
        };
      case 'zh-TW':
        return {
          displayName: restaurant.nameZhTw || restaurant.nameEn || name,
          displayDescription: restaurant.introductionTitleZhTw || restaurant.introductionTitleEn || description
        };
      case 'en':
      default:
        return {
          displayName: restaurant.nameEn || name,
          displayDescription: restaurant.introductionTitleEn || description
        };
    }
  }, [restaurant, currentLang, isFromCategory]);
  
  // 통화에 맞는 최소 주문 금액 필드 결정
  const getMinOrderAmountByCurrency = useMemo(() => {
    switch (effectiveCurrency) {
      case 'KRW':
        return restaurant.minOrderAmountKrw !== undefined ? restaurant.minOrderAmountKrw : restaurant.minOrderAmount;
      case 'USD':
        return restaurant.minOrderAmountUsd !== undefined ? restaurant.minOrderAmountUsd : restaurant.minOrderAmount;
      case 'JPY':
        return restaurant.minOrderAmountJpy !== undefined ? restaurant.minOrderAmountJpy : restaurant.minOrderAmount;
      case 'CNY':
        return restaurant.minOrderAmountCny !== undefined ? restaurant.minOrderAmountCny : restaurant.minOrderAmount;
      case 'TWD':
        return restaurant.minOrderAmountTwd !== undefined ? restaurant.minOrderAmountTwd : restaurant.minOrderAmount;
      default:
        return restaurant.minOrderAmount;
    }
  }, [restaurant, effectiveCurrency]);
  
  // 최소 주문 금액 표시 텍스트
  const minOrderText = getMinOrderAmountByCurrency > 0 
    ? `${t('restaurant.minOrder')}: ${formatPriceWithCurrency(getMinOrderAmountByCurrency, effectiveCurrency)}` 
    : t('restaurant.noMinimumOrder');

  return (
    <div className="rounded-lg overflow-hidden bg-white shadow">
      {/* 레스토랑 헤더 섹션 - 더 짧은 높이와 강조된 디자인 */}
      <div className="relative">
        {/* 배경 이미지 - 높이 줄임 */}
        <div className="h-40 overflow-hidden">
          <img 
            src={restaurant.backgroundUrl || restaurant.logoUrl} 
            alt={displayName}
            className="w-full h-full object-cover"
          />
          {/* 반투명 오버레이로 텍스트 가독성 향상 */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80"></div>
        </div>
        
        {/* 이름 오버레이 */}
        <div className="absolute bottom-0 left-0 w-full p-4">
          <h2 className="text-xl font-bold text-white">{displayName}</h2>
        </div>
      </div>
      
      {/* 레스토랑 정보 섹션 */}
      <div className="p-3">
        {/* 설명 텍스트 - 한 줄로 제한하여 높이 감소 */}
        {restaurant.id === 'gift-shop-1' ? (
          <div className="text-gray-700 mb-4 text-sm leading-relaxed">
            {getGiftShopDescription()}
          </div>
        ) : (
          <p className="text-gray-600 mb-3 text-sm overflow-hidden whitespace-nowrap text-ellipsis">
            {displayDescription}
          </p>
        )}
        
        {/* 하단 정보 영역 - RestaurantItem과 동일한 정보 유지하되 디자인 차별화 */}
        <div className="flex justify-between items-center">
          {/* 배달 뱃지 영역 (좌측) - gift-shop-1이 아닌 경우에만 */}
          {restaurant.id === 'gift-shop-1' ? (
            <div 
              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-50 text-red-600 cursor-pointer hover:bg-red-100"
              onClick={openChannelTalk}
            >
              {getChatText()}
            </div>
          ) : (
            <div className="flex space-x-1">
              {deliveryStatus.canOrderNow && (
                <div className="inline-flex items-center gap-0.5 bg-emerald-50 border border-emerald-200 px-1.5 py-1 rounded-md text-[10px] font-medium text-emerald-700">
                  <span className="text-[10px]">⚡</span>
                  <span>{t('badge.orderNow')}</span>
                </div>
              )}
              {/* UI 미노출: Later Today 뱃지 - 예약주문 기능 일시적 미노출 */}
              {/* {deliveryStatus.canOrderLater && (
                <div className="inline-flex items-center gap-0.5 bg-blue-50 border border-blue-200 px-1.5 py-1 rounded-md text-[10px] font-medium text-blue-700">
                  <span className="text-[10px]">🕐</span>
                  <span>{t('badge.orderLater')}</span>
                </div>
              )} */}
            </div>
          )}
          
          {/* 최소 주문 금액 (우측) - 디자인 차별화 */}
          <div className="text-sm text-gray-700 font-medium">
            {minOrderText}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantDetailCard; 